import React, { useState } from 'react';
import { Users, X } from 'lucide-react';
import { BRRApiClient } from '../services/api';
import { SessionAuditClient } from '../services/sessionApi';
import { AuditAction } from '../services/sessionStatus';

interface ReassignBRRButtonProps {
  batchId: string;
  sessionId: string;
  currentOwnerEmail: string;
  sessionClient: SessionAuditClient;
  apiClient: BRRApiClient;
  onReassigned: () => void;
  distinctReviewers?: string[];
}

export const ReassignBRRButton: React.FC<ReassignBRRButtonProps> = ({
  batchId,
  sessionId,
  currentOwnerEmail,
  sessionClient,
  apiClient,
  onReassigned,
  distinctReviewers = [],
}) => {
  const [showModal, setShowModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValidEmail = newEmail.includes('@') &&
    newEmail.toLowerCase() !== currentOwnerEmail.toLowerCase() &&
    newEmail.trim().length > 0;

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowModal(true);
    setNewEmail('');
    setError(null);
  };

  const handleCancel = () => {
    setShowModal(false);
    setError(null);
  };

  const handleConfirm = async () => {
    setReassigning(true);
    setError(null);
    try {
      await apiClient.reassignReview({
        batchId,
        sessionId,
        newUserEmail: newEmail.trim(),
      });

      await sessionClient.updateSession(sessionId, {
        userEmail: newEmail.trim(),
      });

      await sessionClient.createAuditLog({
        batchId,
        sessionId,
        action: AuditAction.Reassigned,
        details: JSON.stringify({
          from: currentOwnerEmail,
          to: newEmail.trim(),
          reassignedAt: new Date().toISOString(),
        }),
      });

      setShowModal(false);
      onReassigned();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reassign BRR');
    } finally {
      setReassigning(false);
    }
  };

  const filteredReviewers = distinctReviewers.filter(
    (email) => email.toLowerCase() !== currentOwnerEmail.toLowerCase()
  );

  return (
    <>
      <button
        onClick={handleOpen}
        className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center gap-1"
        title="Reassign BRR"
      >
        <Users size={14} />
        Reassign
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 bg-teal-50 border-b border-teal-100">
              <div className="p-2 bg-teal-100 rounded-full">
                <Users className="text-teal-600" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-teal-900">Reassign BRR</h3>
              <button
                onClick={handleCancel}
                className="ml-auto p-1 hover:bg-teal-100 rounded transition-colors"
                disabled={reassigning}
              >
                <X size={20} className="text-teal-600" />
              </button>
            </div>

            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-1">
                Current owner
              </p>
              <p className="text-sm font-medium text-gray-900 mb-4">
                {currentOwnerEmail}
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reassign to
              </label>

              {filteredReviewers.length > 0 ? (
                <select
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  disabled={reassigning}
                >
                  <option value="">Select a reviewer...</option>
                  {filteredReviewers.map((email) => (
                    <option key={email} value={email}>{email}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  disabled={reassigning}
                />
              )}

              {newEmail && newEmail.toLowerCase() === currentOwnerEmail.toLowerCase() && (
                <p className="mt-2 text-sm text-amber-600">
                  Cannot reassign to the current owner.
                </p>
              )}

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
              <button
                onClick={handleCancel}
                disabled={reassigning}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!isValidEmail || reassigning}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reassigning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Reassigning...
                  </>
                ) : (
                  <>
                    <Users size={16} />
                    Reassign
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
